<template>
  <div class="start-up-report">
    <table v-if="chartData" class="report-table">
      <tr>
        <th></th>
        <th>Design Point</th>
        <th
          class="text-center bg-gray-400"
          :colspan="hypotheticalFrequenciesHz?.length"
        >
          Pump Variable Frequency Analysis
        </th>
      </tr>
      <StartUpReportRow
        title="Frequency"
        field="pumpHz"
        :baseUnit="Hertz"
        :targetUnit="Hertz"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Operating HP Required"
        field="totalRequiredBrakeHP"
        :baseUnit="PowerUnits.HorsePower"
        :targetUnit="targetPowerUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Liquid Flow"
        field="liquidFlowRateSTBD"
        :baseUnit="LiquidFlowRateUnits.BarrelsPerDay"
        :targetUnit="targetLiquidFlowUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Percent Motor Load"
        field="motorLoadRatio"
        :baseUnit="PercentageUnits.Ratio"
        :targetUnit="PercentageUnits.Percentage"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Operating Amps"
        field="motorCurrentA"
        :baseUnit="CurrentUnits.Amperes"
        :targetUnit="targetCurrentUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Velocity Past Mtr"
        field="flowVelocityAroundMotorFTSEC"
        :baseUnit="SpeedUnits.FeetPerSecond"
        :targetUnit="targetSpeedUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Surface Volts Required"
        field="requiredVoltageV"
        :baseUnit="VoltageUnits.Volts"
        :targetUnit="targetVoltageUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Surface kVA Required"
        field="requiredKVA"
        :baseUnit="GenericUnits.Generic"
        :targetUnit="GenericUnits.Generic"
        :round="true"
        :genericLabel="`kVA`"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Percent VFD Load"
        field="vfdLoadRatio"
        :baseUnit="PercentageUnits.Ratio"
        :targetUnit="PercentageUnits.Percentage"
        :round="true"
        :data="allReportCalcs"
      />
    </table>
  </div>
</template>

<script setup lang="ts">
import { PercentageUnits } from "@/libs/units/percentage";
import { Hertz } from "@/libs/units/frequency";
import { PowerUnits } from "@/libs/units/power";
import { CurrentUnits } from "@/libs/units/current";
import { SpeedUnits } from "@/libs/units/speed";
import { VoltageUnits } from "@/libs/units/voltage";
import { computed } from "vue";
import StartUpReportRow from "./StartUpReportRow.vue";
import { GenericUnits } from "@/libs/units/generic";
import type {
  PumpTornadoChartViewData,
  StartupBhpReportCalcRow
} from "@/libs/charting";
import { usePumpChartingData } from "@/libs/charting";
import { LiquidFlowRateUnits } from "@/libs/units/flowRate";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import {
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  minFrequency: number;
  maxFrequency: number;
  scenario?: Scenario | null;
  sizing?: Sizing | null;
  scenarioCalculations?: ScenarioCalculations;
  pumpTornadoChartData?: PumpTornadoChartViewData;
}>();

const _scenario = props.scenario ?? injectStrict(scenarioKey);
const _sizing = props.sizing ?? injectStrict(sizingKey, null);
const _scenarioCalculations =
  props.scenarioCalculations ?? injectStrict(scenarioCalculationsKey);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getCurrentUnit,
  getSpeedUnit,
  getVoltageUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(_scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetCurrentUnit = getCurrentUnit("Motor Current.convert.A");
const targetSpeedUnit = getSpeedUnit("Flow by Motor.convert.ft/s");
const targetVoltageUnit = getVoltageUnit("Surface Voltage.convert.V");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const chartData = computed(() => {
  if (!props.pumpTornadoChartData) {
    return usePumpChartingData(
      props.minFrequency,
      props.maxFrequency,
      _scenarioCalculations,
      targetLiquidFlowUnit.value,
      targetPowerUnit.value,
      targetLargeLengthUnit.value,
      targetPressureUnit.value
    );
  } else {
    return props.pumpTornadoChartData;
  }
});
const hypotheticalFrequenciesHz = computed(() => {
  return chartData.value?.context.hypotheticalPumpFrequenciesHz;
});

const allReportCalcs = computed<StartupBhpReportCalcRow[]>(
  () => chartData.value?.bhpStartupRows || []
);
</script>
